// Company stores (S4 shop, 2026-10-12) — /store/orders: the person's own
// store orders, and one order at /store/orders?order=<id> (the link the
// order emails and Stripe use; an admin can open any of the store's orders).
//
// Coming back from Stripe Checkout: `&paid=1` → "thanks", and the order is
// re-read every couple of seconds until the payment webhook marks it paid;
// `&cancelled=1` (or back without paying) → nothing was charged, pay when
// ready. "Pay now" appears only when this person is the one who pays.
//
// A store admin also gets a "Mine / Everyone's" switch (?view=everyone):
// every order in the store (GET /portal/store/admin/orders), with who
// ordered each one (UX review 2026-10-12).
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PortalLayout } from "@/components/PortalLayout";
import { StoreGate, StoreHeading } from "@/components/StoreGate";
import { StoreCancelOrder } from "@/components/StoreCancelOrder";
import { CARD, H2, LINK, Notice, OrderMoney, PRIMARY, SECONDARY, StatusPill } from "@/components/StoreShopUi";
import { apiMessage, formatDate, type MyStore } from "@/lib/portal-store";
import {
  fetchAdminOrders,
  fetchMyOrders,
  fetchStoreOrder,
  itemsSummary,
  money,
  payStoreOrder,
  paymentMethodWords,
  STORE_ORDERS_KEY,
  storeOrderKey,
  type StoreOrder,
} from "@/lib/store-shop";

const POLLS = 8;
const POLL_MS = 2_000;

const EVERYONE_KEY = ["portal-store-admin-orders", "all"] as const;

export function StoreOrdersPage() {
  const [params, setParams] = useSearchParams();
  const orderId = params.get("order");
  return (
    <PortalLayout>
      <StoreGate>
        {(my) => {
          const everyone = my.role === "admin" && params.get("view") === "everyone";
          return (
            <>
              <StoreHeading store={my.store} title={orderId ? (my.role === "admin" ? "Order" : "Your order") : everyone ? "Everyone's orders" : "Your orders"} />
              {orderId ? (
                <OrderDetail key={orderId} my={my} id={orderId} paid={params.get("paid") === "1"} cancelled={params.get("cancelled") === "1"} />
              ) : (
                <>
                  {my.role === "admin" && (
                    <ViewSwitch everyone={everyone} onChange={(e) => setParams(e ? { view: "everyone" } : {}, { replace: true })} />
                  )}
                  <OrdersList everyone={everyone} />
                </>
              )}
            </>
          );
        }}
      </StoreGate>
    </PortalLayout>
  );
}

/** Admin only: my orders, or every order in the store. */
function ViewSwitch({ everyone, onChange }: { everyone: boolean; onChange: (everyone: boolean) => void }) {
  const tab = (on: boolean) =>
    "min-h-[36px] rounded px-3.5 text-sm transition-colors " + (on ? "bg-ink font-semibold text-white" : "text-ink hover:bg-canvas");
  return (
    <div role="tablist" aria-label="Whose orders" className="mb-4 inline-flex gap-1 rounded-md border border-rule bg-white p-1">
      <button type="button" role="tab" aria-selected={!everyone} className={tab(!everyone)} onClick={() => onChange(false)}>
        Mine
      </button>
      <button type="button" role="tab" aria-selected={everyone} className={tab(everyone)} onClick={() => onChange(true)}>
        Everyone's
      </button>
    </div>
  );
}

/** "Pay now" → a Stripe Checkout for this person → off to Stripe. */
export function usePayNow() {
  const going = useRef(false);
  const [redirecting, setRedirecting] = useState(false);
  const pay = useMutation({
    mutationFn: (id: string) => payStoreOrder(id),
    onSuccess: (s) => {
      setRedirecting(true);
      window.location.assign(s.url);
    },
    onError: () => {
      going.current = false;
    },
  });
  const start = (id: string) => {
    if (going.current) return;
    going.current = true;
    pay.mutate(id);
  };
  return { start, pay, busy: pay.isPending || redirecting, redirecting };
}

function OrdersList({ everyone = false }: { everyone?: boolean }) {
  const q = useQuery({
    queryKey: everyone ? EVERYONE_KEY : STORE_ORDERS_KEY,
    queryFn: () => (everyone ? fetchAdminOrders() : fetchMyOrders()),
    staleTime: 0,
  });
  const payNow = usePayNow();
  if (q.isPending) return <div className="h-24 animate-pulse rounded-lg bg-cream-300" aria-label="Loading" />;
  if (q.isError) return <p className="rounded-lg border border-rule bg-white p-6 text-sm text-rust-500">{apiMessage(q.error)}</p>;
  if (q.data.length === 0) {
    return (
      <div className={CARD}>
        <p className="text-sm text-brown-400">
          {everyone ? "Nobody has ordered from the store yet." : "No orders yet."} <Link to="/store" className={LINK}>Browse the store</Link>
        </p>
      </div>
    );
  }
  return (
    <section className="space-y-3">
      {payNow.pay.isError && <Notice tone="risk">{apiMessage(payNow.pay.error)}</Notice>}
      <ul className="space-y-3">
        {q.data.map((o) => (
          <li key={o.id} className={CARD}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Link to={`/store/orders?order=${encodeURIComponent(o.id)}`} className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-ink">Order {o.reference}</span>
                  <span className="text-xs text-brown-400">{formatDate(o.createdAt)}</span>
                  {everyone && (
                    <span className="text-xs text-brown-400">· {o.mine ? "you" : o.member?.name || o.member?.email || "someone"}</span>
                  )}
                </div>
                <p className="mt-1 truncate text-sm text-brown-400">{itemsSummary(o.items)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <StatusPill order={o} />
                  {o.total != null && <span className="text-sm font-semibold tabular-nums text-ink">{money(o.total)}</span>}
                </div>
              </Link>
              {o.canPay && (
                <button type="button" className={PRIMARY} disabled={payNow.busy} onClick={() => payNow.start(o.id)}>
                  {payNow.redirecting ? "Taking you to payment…" : "Pay now"}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function OrderDetail({ my, id, paid, cancelled }: { my: MyStore; id: string; paid: boolean; cancelled: boolean }) {
  const [polls, setPolls] = useState(0);
  const q = useQuery({
    queryKey: storeOrderKey(id),
    queryFn: () => fetchStoreOrder(id),
    staleTime: 0,
  });
  const o = q.data ?? null;
  const settled = !!o && ["paid", "placed", "approved"].includes(o.status);
  const waitingForWebhook = paid && !!o && !settled && (o.status === "awaiting_payment" || o.status === "paying");

  // Back from Stripe with a payment: re-read until the webhook lands.
  const refetch = q.refetch;
  useEffect(() => {
    if (!waitingForWebhook || polls >= POLLS) return;
    const t = window.setTimeout(() => {
      setPolls((n) => n + 1);
      void refetch();
    }, POLL_MS);
    return () => window.clearTimeout(t);
  }, [waitingForWebhook, polls, refetch]);

  const payNow = usePayNow();

  if (q.isPending) return <div className="h-40 animate-pulse rounded-lg bg-cream-300" aria-label="Loading" />;
  if (q.isError) return <p className="rounded-lg border border-rule bg-white p-6 text-sm text-rust-500">{apiMessage(q.error)}</p>;
  if (!o) {
    return (
      <div className={CARD}>
        <p className="text-sm text-brown-400">
          We couldn't find that order. <Link to="/store/orders" className={LINK}>See your orders</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link to="/store/orders" className="inline-block text-sm text-brown-400 hover:text-ink">
        ← Your orders
      </Link>

      {paid && settled && <Notice tone="ok">Thank you — your payment went through. Your order is on its way into production.</Notice>}
      {waitingForWebhook && polls < POLLS && <Notice tone="plain">Thank you — we're confirming your payment. This takes a few seconds.</Notice>}
      {waitingForWebhook && polls >= POLLS && (
        <Notice tone="plain">Your payment is still being confirmed — it can take a minute or two. There's no need to pay again; this page will show Paid once it's through.</Notice>
      )}
      {!paid && cancelled && o.status === "awaiting_payment" && (
        <Notice tone="plain">Payment wasn't finished, so nothing was charged. You can pay whenever you're ready.</Notice>
      )}

      <section className={`${CARD} space-y-4`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl font-bold tracking-tight">Order {o.reference}</h2>
            <p className="text-xs text-brown-400">
              Placed {formatDate(o.createdAt)}
              {o.member?.name && !o.mine ? ` by ${o.member.name}` : ""}
              {o.paymentMethod ? ` · ${paymentMethodWords(o.paymentMethod)}` : ""}
            </p>
          </div>
          <StatusPill order={o} />
        </div>
        {o.status === "rejected" && o.rejectedReason && <Notice tone="risk">Declined: {o.rejectedReason}</Notice>}
        {o.canPay && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button type="button" className={PRIMARY} disabled={payNow.busy} onClick={() => payNow.start(o.id)}>
              {payNow.redirecting ? "Taking you to payment…" : `Pay now${o.total != null ? ` — ${money(o.total)}` : ""}`}
            </button>
            {payNow.pay.isError && <p className="text-sm text-rust-500">{apiMessage(payNow.pay.error)}</p>}
          </div>
        )}
        {my.role === "admin" && (
          <div>
            <StoreCancelOrder order={o} />
          </div>
        )}
      </section>

      <section className={CARD}>
        <h2 className={H2}>Items</h2>
        <ul className="mt-2 divide-y divide-rule">
          {o.items.map((it, i) => (
            <li key={`${it.productId}-${i}`} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">{it.name}</p>
                <p className="text-xs text-brown-400">
                  {it.quantity} {it.quantity === 1 ? "piece" : "pieces"}
                  {it.colours.length ? ` · ${it.colours.join(", ")}` : ""}
                </p>
              </div>
              <p className="shrink-0 text-right text-sm tabular-nums">
                <span className="block font-semibold text-ink">{money(it.total)}</span>
                <span className="text-xs text-brown-400">{money(it.each)} each</span>
              </p>
            </li>
          ))}
        </ul>
        <div className="mt-3 border-t border-rule pt-3">
          <OrderMoney order={o} />
        </div>
      </section>

      {o.shipTo && (
        <section className={CARD}>
          <h2 className={H2}>Ship to</h2>
          <address className="mt-1 text-sm not-italic text-ink">
            {o.shipTo.name}
            <br />
            {o.shipTo.street1}
            {o.shipTo.street2 ? (
              <>
                <br />
                {o.shipTo.street2}
              </>
            ) : null}
            <br />
            {o.shipTo.city}, {o.shipTo.stateCode} {o.shipTo.postalCode}
          </address>
        </section>
      )}

      <div>
        <Link to="/store" className={SECONDARY}>
          Back to the store
        </Link>
      </div>
    </div>
  );
}
